import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronLeft, FileCheck, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp, Download, Home, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

const BatchResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { batchResult, visaType } = location.state || {};
  const { isAuthenticated, user } = useApp();
  const { trackEvent } = useAnalytics();
  
  const [expandedDoc, setExpandedDoc] = useState(null);
  const emailSent = batchResult?.email_sent;

  useEffect(() => {
    if (!batchResult) {
      navigate('/');
      return;
    }
    
    // Track results view
    trackEvent(ANALYTICS_EVENTS.RESULTS_VIEW, {
      document_count: batchResult?.results?.length || 0,
      average_score: batchResult?.average_readiness_score || 0,
      visa_type: visaType
    });
  }, [batchResult, navigate, trackEvent, visaType]);

  // Calculate overall status
  const overallStatus = useMemo(() => {
    if (!batchResult?.results) return 'unknown';
    
    const analyses = batchResult.results
      .filter(r => r.status === 'success')
      .map(r => r.analysis);
    
    if (analyses.length === 0) return 'error';
    
    const avgScore = batchResult.average_readiness_score || 0;
    const hasCritical = analyses.some(a => a.critical_issues?.length > 0);
    
    if (avgScore >= 80 && !hasCritical) return 'ready';
    if (avgScore >= 50 || !hasCritical) return 'warning';
    return 'reject-risk';
  }, [batchResult]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-verify text-verify-foreground';
      case 'warning': return 'bg-warning text-warning-foreground';
      case 'reject-risk': return 'bg-destructive text-destructive-foreground';
      default: return 'bg-slate-500 text-white';
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'ready': return 'border-verify/30';
      case 'warning': return 'border-warning/30';
      case 'reject-risk': return 'border-destructive/30';
      default: return 'border-slate-300';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-verify';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  if (!batchResult) {
    return null;
  }

  const successfulResults = batchResult.results?.filter(r => r.status === 'success') || [];
  const failedResults = batchResult.results?.filter(r => r.status === 'error') || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="home-button"
          >
            <Home className="w-5 h-5" />
            <span className="font-public">Home</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-20" aria-hidden="true" />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Overall Summary */}
          <div className={`rounded-xl p-8 mb-8 border-2 ${getStatusBorderColor(overallStatus)} bg-gradient-to-br from-white to-slate-50`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2">
                  Document Analysis Complete
                </h1>
                <p className="font-public text-lg text-slate-600">
                  {visaType || 'Spain Visa'} • {batchResult.total_documents} document{batchResult.total_documents > 1 ? 's' : ''} analyzed
                </p>
              </div>
              
              <div className="text-center">
                <div className={`text-6xl font-outfit font-bold ${getScoreColor(batchResult.average_readiness_score)}`}>
                  {batchResult.average_readiness_score}%
                </div>
                <p className="text-sm text-slate-500 mt-1">Average Readiness</p>
                <span className={`inline-block mt-2 px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(overallStatus)}`}>
                  {overallStatus === 'ready' ? 'Low Risk' : 
                   overallStatus === 'warning' ? 'Medium Risk' : 
                   overallStatus === 'reject-risk' ? 'High Risk' : 'Unknown'}
                </span>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-slate-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-verify">{batchResult.processed}</div>
                <p className="text-sm text-slate-500">Processed</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">
                  {successfulResults.reduce((sum, r) => sum + (r.analysis?.warnings?.length || 0), 0)}
                </div>
                <p className="text-sm text-slate-500">Warnings</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">
                  {successfulResults.reduce((sum, r) => sum + (r.analysis?.critical_issues?.length || 0), 0)}
                </div>
                <p className="text-sm text-slate-500">Critical Issues</p>
              </div>
            </div>
          </div>

          {/* Email Notification Banner */}
          {emailSent && isAuthenticated && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-trust/10 border border-trust/30 rounded-lg p-4 mb-6 flex items-center gap-3"
            >
              <Mail className="w-5 h-5 text-trust flex-shrink-0" />
              <div>
                <p className="font-public font-medium text-slate-900">
                  Results sent to {user?.email}
                </p>
                <p className="text-sm text-slate-600">
                  Check your email for a summary of this analysis.
                </p>
              </div>
            </motion.div>
          )}

          {/* Failed Documents Warning */}
          {failedResults.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-public font-semibold text-destructive">
                    {failedResults.length} document{failedResults.length > 1 ? 's' : ''} failed to process
                  </p>
                  <ul className="text-sm text-slate-700 mt-2 space-y-1">
                    {failedResults.map((r, idx) => (
                      <li key={idx}>• {r.error || 'Unknown error'}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Document Results */}
          <div className="space-y-4">
            <h2 className="font-outfit font-bold text-xl text-slate-900">Document Details</h2>
            
            {successfulResults.map((result, idx) => {
              const analysis = result.analysis;
              const isExpanded = expandedDoc === idx;
              
              return (
                <motion.div
                  key={result.pending_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`bg-white border rounded-lg overflow-hidden ${getStatusBorderColor(analysis.status)}`}
                >
                  {/* Document Header - Always Visible */}
                  <button
                    onClick={() => setExpandedDoc(isExpanded ? null : idx)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`expand-doc-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        analysis.status === 'ready' ? 'bg-verify/10' :
                        analysis.status === 'warning' ? 'bg-warning/10' : 'bg-destructive/10'
                      }`}>
                        <FileCheck className={`w-6 h-6 ${
                          analysis.status === 'ready' ? 'text-verify' :
                          analysis.status === 'warning' ? 'text-warning' : 'text-destructive'
                        }`} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-outfit font-bold text-lg text-slate-900">
                          {analysis.document_type}
                        </h3>
                        <p className="text-sm text-slate-500">{analysis.filename}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getScoreColor(analysis.readiness_score)}`}>
                          {analysis.readiness_score}%
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-slate-200"
                      >
                        <div className="p-6 space-y-6">
                          {/* Critical Issues */}
                          {analysis.critical_issues?.length > 0 && (
                            <div>
                              <h4 className="font-public font-semibold text-destructive mb-3 flex items-center gap-2">
                                <XCircle className="w-4 h-4" />
                                Critical Issues ({analysis.critical_issues.length})
                              </h4>
                              <div className="space-y-2">
                                {analysis.critical_issues.map((issue, i) => (
                                  <div key={i} className="bg-destructive/5 border border-destructive/20 rounded-md p-3">
                                    <p className="font-medium text-slate-900">{issue.title || issue}</p>
                                    {issue.description && (
                                      <p className="text-sm text-slate-600 mt-1">{issue.description}</p>
                                    )}
                                    {issue.recommendation && (
                                      <p className="text-sm text-destructive mt-2 font-medium">
                                        Fix: {issue.recommendation}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Warnings */}
                          {analysis.warnings?.length > 0 && (
                            <div>
                              <h4 className="font-public font-semibold text-warning mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Warnings ({analysis.warnings.length})
                              </h4>
                              <div className="space-y-2">
                                {analysis.warnings.map((warning, i) => (
                                  <div key={i} className="bg-warning/5 border border-warning/20 rounded-md p-3">
                                    <p className="font-medium text-slate-900">{warning.title || warning}</p>
                                    {warning.description && (
                                      <p className="text-sm text-slate-600 mt-1">{warning.description}</p>
                                    )}
                                    {warning.recommendation && (
                                      <p className="text-sm text-warning mt-2 font-medium">
                                        Suggestion: {warning.recommendation}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Passed Checks */}
                          {analysis.passed_checks?.length > 0 && (
                            <div>
                              <h4 className="font-public font-semibold text-verify mb-3 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Passed Checks ({analysis.passed_checks.length})
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {analysis.passed_checks.map((check, i) => (
                                  <div key={i} className="bg-verify/5 border border-verify/20 rounded-md p-3">
                                    <p className="font-medium text-slate-900">{check.title || check}</p>
                                    {check.description && (
                                      <p className="text-sm text-slate-600 mt-1">{check.description}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Document Metadata */}
                          <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-public font-semibold text-slate-700 mb-2">Document Details</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Language</p>
                                <p className="font-medium">{analysis.detected_language || 'Unknown'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Confidence</p>
                                <p className="font-medium">{analysis.type_confidence || 0}%</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Official Stamps</p>
                                <p className="font-medium">{analysis.has_official_stamps ? 'Yes' : 'No'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Signatures</p>
                                <p className="font-medium">{analysis.has_signatures ? 'Yes' : 'No'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col md:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/select-country')}
              className="px-6 py-3 bg-trust text-trust-foreground rounded-md font-public font-semibold hover:bg-trust/90 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="analyze-more-button"
            >
              <FileCheck className="w-5 h-5" />
              Analyze More Documents
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-md font-public font-medium hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="back-home-button"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </button>
          </div>

          {/* Privacy Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              All uploaded documents have been securely deleted after analysis.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="results-disclaimer">
            <p className="text-xs text-slate-500 text-center">
              <strong>Disclaimer:</strong> This analysis identifies common issues and potential risks based on typical visa requirements. 
              It is not legal advice, does not guarantee visa approval, and should not replace consultation with immigration professionals. 
              Requirements may vary by consulate and individual circumstances.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BatchResultsPage;
